import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ExceptionService, UtilsService } from '../../xform-compat';
import { RmsBasicCrudService } from '../components/rms-basic-crud.service';
import { PositionNote, PositionNoteType } from '../../shared/models';

/**
 * Service for managing position notes (notes and actions attached to positions).
 */
@Injectable({
  providedIn: 'root'
})
export class PositionNoteService extends RmsBasicCrudService<PositionNote> {

  private readonly basePath = '/api/portfolio/position-notes';

  constructor(
    http: HttpClient,
    utilsService: UtilsService,
    exceptionService: ExceptionService
  ) {
    super(http, utilsService, exceptionService);
  }

  /** Loads all active notes for a set of position IDs */
  public getNotesByPositionIds(positionIds: Array<number>): Observable<Array<PositionNote>> {
    return this.getHttp().post<Array<PositionNote>>(`${this.basePath}/search`, positionIds)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  /** Creates a new position note */
  public create(note: Partial<PositionNote>): Observable<PositionNote> {
    return this.getHttp().post<PositionNote>(this.basePath, note)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  /** Updates an existing position note (deactivates old, creates new) */
  public update(id: number, note: Partial<PositionNote>): Observable<PositionNote> {
    return this.getHttp().put<PositionNote>(`${this.basePath}/${id}`, note)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  /** Soft-deletes a position note */
  public delete(id: number): Observable<void> {
    return this.getHttp().delete<void>(`${this.basePath}/${id}`)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  /** Bulk-deletes all position notes of the given type for the specified position IDs */
  public bulkDeleteByType(positionIds: Array<number>, noteType: PositionNoteType): Observable<void> {
    return this.getHttp().post<void>(`${this.basePath}/bulk-delete`, { positionIds, positionNoteType: noteType })
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }
}
